import { useQuery } from "@tanstack/react-query";
import { qk } from "../../lib/queryKeys";
import { getJson } from "../../lib/http";
import { useCompaniesStore } from "../../store/admin/companiesStore";
import type { Unit } from "../../store/admin/unitsStore";

export interface UnitListParams {
  page?: number;
  limit?: number;
  search?: string;
  status?: string;
  type?: string;
}

interface ListMeta {
  error: string | null;
  count: number;
  status: number;
  currentPage: number;
  totalPages: number;
  totalItems: number;
  url: string;
}

interface VehicleListBody {
  data?: Unit[];
  meta?: ListMeta;
  next?: { page?: number } | boolean;
  total?: number;
}

export interface UnitListResult {
  data: Unit[];
  meta?: ListMeta;
}

function normalize(body: VehicleListBody, params: UnitListParams): UnitListResult {
  const data = body.data ?? [];
  const limit = params.limit ?? 8;
  const meta =
    body.meta ??
    (body.next
      ? {
          currentPage: params.page ?? 1,
          totalPages: Math.ceil((body.total ?? data.length) / limit),
          totalItems: body.total ?? data.length,
          count: data.length,
          status: 200,
          error: null,
          url: "/vehicleList",
        }
      : undefined);
  return { data, meta };
}

export function useUnitsList(params: UnitListParams = {}) {
  const cid = useCompaniesStore((s) => s.companyId);
  return useQuery({
    queryKey: qk.vehicles.list(cid, params),
    queryFn: ({ signal }) =>
      getJson<VehicleListBody>(
        "/vehicleList",
        { page: 1, limit: 8, ...params },
        { signal },
      ).then((body) => normalize(body, params)),
  });
}

export function allUnitsQueryOptions(cid: string | null) {
  return {
    queryKey: qk.vehicles.all(cid),
    queryFn: ({ signal }: { signal?: AbortSignal }) =>
      getJson<VehicleListBody>(
        "/vehicleList",
        { page: 1, limit: 1000 },
        { signal },
      ).then((body) => body.data ?? []),
    enabled: !!cid,
    staleTime: 5 * 60_000,
  };
}

export function useAllUnits() {
  const cid = useCompaniesStore((s) => s.companyId);
  return useQuery(allUnitsQueryOptions(cid));
}
